import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tryParseInt } from 'fym-common/src/utils/tryParseInt';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { FullScreenUpgradeHost } from '../../music24/components/SMLUpgradeModal/FullScreenUpgradeHost';
import { useGetCatalogQuery } from '../../../redux/api/fymAPI';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { useAccountId } from '../../../redux/slices/appInfo/hooks';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useChecklistState } from '../../onboarding/checklist/useChecklistState';
import { useOpenReleasePage } from '../../music24/promote/releases/useOpenReleasePage';
import { useOpenSongPage } from '../catalog/song/page/useOpenSongPage';
import { catalogAddSongPath } from '../catalog/addSong/catalogAddSongPath';
import { useOpenSmartLinkSheetForSong } from '../creationSheet/useOpenSmartLinkSheetForSong';
import { pickableSongs, presaveSongState, todayUtc } from './presaveCreateFacts';
import { PresaveTopBar } from './PresaveTopBar';
import { PresaveFromSong } from './PresaveFromSong';
import { PresaveCreated } from './PresaveCreated';
import { PresaveSongPicker } from './PresaveSongPicker';
import { PresaveHasPage, PresaveReleased, PresaveSongMissing } from './PresaveSongElsewhere';
import { CreatedPresave } from './useCreatePresaveFromSong';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './PresaveCreate.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The query value of the song (the old wizard read the same one). */
const SONG_PARAM = 'catalogItemUuid';

interface CreatedState {
  item: CatalogItem;
  page: CreatedPresave;
  services: string[];
}

/**
 * The pre-save screen, `/app/smartlinks/presaveLink?accountId=&catalogItemUuid=` (design
 * presave-create). A full page with no sidebar, like Add my song. With a song it makes the page from
 * that song; with none it first picks or adds a song. It replaces the typed pre-save wizard.
 */
const PresaveCreateViewComponent: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const router = useSMLRouter();
  const activeAccountId = useAccountId();
  const accountId = tryParseInt(params.get('accountId') ?? undefined) ?? activeAccountId ?? undefined;
  const songUuid = params.get(SONG_PARAM) ?? undefined;
  const today = useMemo(() => todayUtc(Date.now()), []);
  const [created, setCreated] = useState<CreatedState | null>(null);

  // A cover made in the cover editor (another tab) shows here when the artist comes back.
  const catalog = useGetCatalogQuery(
    { accountId: accountId ?? 0 },
    { skip: accountId === undefined, refetchOnFocus: true }
  );
  const { state: onboarding } = useChecklistState();
  const openSongPage = useOpenSongPage();
  const openReleasePage = useOpenReleasePage({ accountId: accountId ?? null });
  const openSmartLinkSheet = useOpenSmartLinkSheetForSong();

  const items = catalog.data?.items;
  const item = useMemo(() => items?.find((one) => one.uuid === songUuid), [items, songUuid]);
  const songs = useMemo(() => pickableSongs(items ?? []), [items]);

  const setSong = useCallback(
    (uuid: string | undefined) => {
      const next = new URLSearchParams(params);
      if (uuid === undefined) {
        next.delete(SONG_PARAM);
      } else {
        next.set(SONG_PARAM, uuid);
      }
      setCreated(null);
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate({ search: `?${next.toString()}` }, { replace: true });
    },
    [navigate, params]
  );

  const close = useCallback(() => router.goBack(SMLRoutes.smartlinks), [router]);
  const pickAnother = useCallback(() => setSong(undefined), [setSong]);
  const onPick = useCallback((one: CatalogItem) => setSong(one.uuid), [setSong]);
  const onAddSong = useCallback(() => {
    // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
    void navigate(catalogAddSongPath({ from: 'presave', accountId }));
  }, [accountId, navigate]);
  const onSmartLinkNoSong = useCallback(() => router.push(SMLRoutes.smartlinks), [router]);
  const openCreator = useCallback(
    (smartLinkId: number) => {
      if (accountId !== undefined) {
        router.push(SMLRoutes.details, { accountId, smartLinkId });
      }
    },
    [accountId, router]
  );
  const onCreated = useCallback(
    (page: CreatedPresave, services: string[]) => {
      if (item !== undefined) {
        setCreated({ item, page, services });
      }
    },
    [item]
  );

  const shownItem = created?.item ?? item;
  const songUuidOfPage = shownItem?.uuid ?? '';
  const onOpenSong = useCallback(
    (section?: 'details') => openSongPage(songUuidOfPage, section),
    [openSongPage, songUuidOfPage]
  );
  const onOpenStudio = useCallback(
    () => router.push(SMLRoutes.promoteStudio, { catalogItemUuid: songUuidOfPage }),
    [router, songUuidOfPage]
  );
  const onOpenRelease = useCallback(() => openReleasePage(songUuidOfPage), [openReleasePage, songUuidOfPage]);
  const onHome = useCallback(() => router.push(SMLRoutes.home), [router]);
  const onEditCreated = useCallback(() => {
    if (created !== null) {
      openCreator(created.page.smartLinkId);
    }
  }, [created, openCreator]);
  const onEditExisting = useCallback(() => {
    if (item?.smartLinkId !== null && item?.smartLinkId !== undefined) {
      openCreator(item.smartLinkId);
    }
  }, [item, openCreator]);
  const onSmartLinkForSong = useCallback(
    () => openSmartLinkSheet(songUuidOfPage),
    [openSmartLinkSheet, songUuidOfPage]
  );

  let content: React.ReactNode;
  if (accountId === undefined || (catalog.isLoading && created === null)) {
    content = <LoadingView />;
  } else if (catalog.error !== undefined && items === undefined) {
    content = <ErrorView error={catalog.error} retry={() => catalog.refetch().catch(console.error)} />;
  } else if (created !== null) {
    content = (
      <PresaveCreated
        item={created.item}
        page={created.page}
        services={created.services}
        isFirstRelease={onboarding?.firstReleaseUuid === created.item.uuid && onboarding.checklist !== null}
        onOpenStudio={onOpenStudio}
        onEditPage={onEditCreated}
        onOpenRelease={onOpenRelease}
        onHome={onHome}
        onDone={close}
      />
    );
  } else if (songUuid === undefined) {
    content = <PresaveSongPicker songs={songs} onPick={onPick} onAddSong={onAddSong} onSmartLink={onSmartLinkNoSong} />;
  } else if (item === undefined) {
    content = <PresaveSongMissing onPickSong={pickAnother} />;
  } else {
    const state = presaveSongState(item, today);
    if (state === 'hasPage') {
      content = (
        <PresaveHasPage accountId={accountId} item={item} onEditPage={onEditExisting} onPickAnother={pickAnother} />
      );
    } else if (state === 'released') {
      content = <PresaveReleased item={item} onSmartLink={onSmartLinkForSong} onPickAnother={pickAnother} />;
    } else {
      content = (
        <PresaveFromSong
          accountId={accountId}
          item={item}
          state={state}
          today={today}
          onCreated={onCreated}
          onDraft={openCreator}
          onOpenSongPage={onOpenSong}
          onClose={close}
        />
      );
    }
  }

  return (
    <div className={styles.page} data-testid="presave-create-page">
      <PresaveTopBar title={shownItem?.title} onClose={close} />
      {content}
      {/* This route has no `SMLContainer`: the upgrade prompt and the plan picker need their own mount. */}
      <FullScreenUpgradeHost />
    </div>
  );
};

export const PresaveCreateView = TypedMemo(PresaveCreateViewComponent);
